/* eslint-disable react-hooks/exhaustive-deps */
import { useQuery } from "@apollo/client";
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  Button,
} from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import gql from "graphql-tag";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FavouriteRestaurant } from "../../apollo/server";
import FlashMessage from "../../components/FlashMessage";
import { Header, LoginHeader } from "../../components/Header";
import { ClearCart } from "../../components/Modals";
import { ProductCard } from "../../components/RestaurantComponent";
import { LocationContext } from "../../context/Location";
import UserContext from "../../context/User";
import analytics from "../../utils/analytics";
import { useTranslation } from "react-i18next";

const RESTAURANTS = gql`
  ${FavouriteRestaurant}
`;

function EmptyView() {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 10,
        px: 3,
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 96,
          height: 96,
          borderRadius: "50%",
          backgroundColor: "#fff0f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 3,
        }}
      >
        <FavoriteBorderIcon sx={{ fontSize: 44, color: "#cc0000" }} />
      </Box>
      <Typography variant="h5" fontWeight={700} color="#1a1a1a" mb={1}>
        No favourites yet
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={4} maxWidth={320}>
        Save your favourite restaurants here by tapping the heart icon on any restaurant card.
      </Typography>
      <Button
        variant="contained"
        onClick={() => navigate("/restaurant-list")}
        sx={{
          backgroundColor: "#cc0000",
          borderRadius: "24px",
          px: 4,
          py: 1.2,
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.95rem",
          "&:hover": { backgroundColor: "#aa0000" },
        }}
        startIcon={<StorefrontRoundedIcon />}
      >
        Browse Restaurants
      </Button>
    </Box>
  );
}

function Favourites() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const firstTime = useRef(true);
  const [mainError, setMainError] = useState({});
  const [clearModal, setClearModal] = useState(false);
  const { location } = useContext(LocationContext);
  const [navigateData, setNavigateData] = useState({});
  const { stashCart, restaurant: cartRestaurant, isLoggedIn } = useContext(UserContext);

  const { data, loading, error } = useQuery(RESTAURANTS, {
    variables: {
      longitude: location?.longitude ?? null,
      latitude: location?.latitude ?? null,
    },
    fetchPolicy: "network-only",
  });

  const navigateClearCart = useCallback(async () => {
    // Park it rather than bin it — the customer can come back to this cart.
    await stashCart();
    navigate(`/restaurant/${navigateData.slug}`, { state: navigateData });
  }, [navigateData]);

  const toggleClearCart = useCallback(() => {
    setClearModal((prev) => !prev);
  }, []);

  useEffect(async () => {
    await analytics.track(analytics.events.NAVIGATE_TO_FAVOURITES);
  }, []);

  useEffect(() => {
    if (!firstTime.current && error) {
      setMainError({ type: "error", message: error });
    }
    if (!firstTime.current) firstTime.current = true;
  }, [error]);

  const showMessage = useCallback((messageObj) => {
    setMainError(messageObj);
  }, []);

  const toggleSnackbar = useCallback(() => {
    setMainError({});
  }, []);

  const checkCart = useCallback(
    (id, name, image, slug) => {
      if (cartRestaurant && id !== cartRestaurant) {
        setNavigateData({ id, name, image, slug });
        toggleClearCart();
        return false;
      }
      return true;
    },
    [cartRestaurant]
  );

  const favouriteData = data?.userFavourite ?? [];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f7f7f7" }}>
      {isLoggedIn ? <Header /> : <LoginHeader showIcon />}
      <FlashMessage
        open={Boolean(mainError.type)}
        severity={mainError.type}
        alertMessage={mainError.message}
        handleClose={toggleSnackbar}
      />

      {/* Hero banner */}
      <Box
        sx={{
          mt: "64px",
          background: "linear-gradient(135deg, #cc0000 0%, #ff4444 100%)",
          py: { xs: 4, md: 5 },
          px: { xs: 3, md: 6 },
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <FavoriteIcon sx={{ fontSize: 36, color: "rgba(255,255,255,0.9)" }} />
        <Box>
          <Typography
            variant="h4"
            fontWeight={800}
            color="#fff"
            sx={{ lineHeight: 1.2 }}
          >
            Your Favourites
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", mt: 0.5 }}>
            {loading
              ? "Loading..."
              : favouriteData.length > 0
              ? `${favouriteData.length} saved restaurant${favouriteData.length > 1 ? "s" : ""}`
              : "Restaurants you love, all in one place"}
          </Typography>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "40vh",
            }}
          >
            <CircularProgress sx={{ color: "#cc0000" }} size={48} />
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "40vh",
            }}
          >
            <Typography color="text.secondary">Unable to load favourites. Please try again.</Typography>
          </Box>
        ) : favouriteData.length === 0 ? (
          <EmptyView />
        ) : (
          <Grid container spacing={2}>
            {favouriteData.map((item) => (
              <Grid key={item._id} item xs={12} sm={6} md={4} lg={3}>
                <ProductCard
                  data={item}
                  grid={true}
                  showMessage={showMessage}
                  checkCart={checkCart}
                  cardImageHeight="160px"
                  isSmall={false}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <ClearCart
        isVisible={clearModal}
        toggleModal={toggleClearCart}
        action={navigateClearCart}
      />
    </Box>
  );
}

export default Favourites;
